// eslint-disable-next-line no-unused-vars
import React from 'react';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Topo 3D: linie poziomic widoczne tylko na elementach z targetSelector
const SimpleAnimation = ({ targetSelector = '.anim-target' }) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const clipPathRef = useRef(null);
  const rafRef = useRef(null);
  const maxScrollRef = useRef(1);
  const thresholdsRef = useRef({ start: 0, end: 0 });

  useEffect(() => {
    // Unikalny ID clip-path na wypadek wielu instancji
    const clipId = `simple-anim-clip-${Math.random().toString(36).slice(2, 8)}`;

    // SVG przechowujące definicję clip-path
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', window.innerWidth);
    svg.setAttribute('height', window.innerHeight);
    svg.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;overflow:visible;z-index:-9999;opacity:0;';
    document.body.appendChild(svg);
    svgRef.current = svg;

    const defs = document.createElementNS(svgNS, 'defs');
    svg.appendChild(defs);

    const clipPath = document.createElementNS(svgNS, 'clipPath');
    clipPath.setAttribute('id', clipId);
    clipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');
    defs.appendChild(clipPath);
    clipPathRef.current = clipPath;

    // Kontener fixed — animacja Three.js
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100vw';
    container.style.height = '100vh';
    container.style.zIndex = '9000';
    container.style.pointerEvents = 'none';
    container.style.overflow = 'hidden';
    container.style.opacity = '.7';
    container.style.mixBlendMode = 'multiply';
    container.style.clipPath = `url(#${clipId})`;
    document.querySelector('.main-content').appendChild(container);
    containerRef.current = container;

    // Aktualizacja prostokątów clip-path na podstawie pozycji targetu
    const updateClipRects = () => {
      while (clipPath.firstChild) clipPath.removeChild(clipPath.firstChild);
      const targets = document.querySelectorAll(targetSelector);
      targets.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        const r = document.createElementNS(svgNS, 'rect');
        r.setAttribute('x', rect.left);
        r.setAttribute('y', rect.top);
        r.setAttribute('width', rect.width);
        r.setAttribute('height', rect.height);
        clipPath.appendChild(r);
      });
      svg.setAttribute('width', window.innerWidth);
      svg.setAttribute('height', window.innerHeight);
    };

    // Scena, kamera, renderer
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 1000);
    const camTop = new THREE.Vector3(0, 65, 0.01);
    const camSide = new THREE.Vector3(0, 28, 85);
    camera.position.copy(camTop);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Pomocnicze: konwersja jednostek CSS na px dla progów opacity
    const measureChPx = (ch) => {
      const probe = document.createElement('div');
      probe.style.position = 'absolute';
      probe.style.visibility = 'hidden';
      probe.style.width = `${ch}ch`;
      probe.style.height = '0';
      document.body.appendChild(probe);
      const w = probe.getBoundingClientRect().width;
      document.body.removeChild(probe);
      return w;
    };
    const updateThresholds = () => {
      thresholdsRef.current.start = measureChPx(150);
      thresholdsRef.current.end = window.innerHeight * 2;
    };
    updateThresholds();

    // Parametry terenu i próbkowania
    const size = 140;
    const steps = 128;
    const scale = 0.06;
    const amplitude = 14;

    // Funkcje szumu
    const fracJS = (x) => x - Math.floor(x);
    const hash2 = (x, y) => fracJS(Math.sin(x * 127.1 + y * 311.7) * 43758.5453123);
    const valueNoise2 = (x, y) => {
      const xi = Math.floor(x), yi = Math.floor(y);
      const xf = x - xi, yf = y - yi;
      const a = hash2(xi, yi);
      const b = hash2(xi + 1, yi);
      const c = hash2(xi, yi + 1);
      const d = hash2(xi + 1, yi + 1);
      const ux = xf * xf * (3 - 2 * xf);
      const uy = yf * yf * (3 - 2 * yf);
      return (a * (1 - ux) + b * ux) * (1 - uy) + (c * (1 - ux) + d * ux) * uy;
    };
    const fbm2 = (x, y) => {
      let v = 0, a = 0.5;
      let px = x, py = y;
      for (let i = 0; i < 5; i++) {
        v += a * valueNoise2(px, py);
        const nx = px * 2.0 - py * 0.5;
        const ny = px * 0.5 + py * 2.0;
        px = nx; py = ny;
        a *= 0.5;
      }
      return v;
    };
    const warp2 = (x, y) => {
      const wx = fbm2(x * 1.7 + 3.0, y * 1.7 + 3.0);
      const wy = fbm2(x * 1.7 - 2.4, y * 1.7 - 2.4);
      return [x + 0.6 * wx, y + 0.6 * wy];
    };
    const heightAt = (x, z) => {
      const sx = x * scale, sz = z * scale;
      const w = warp2(sx, sz);
      return fbm2(w[0], w[1]) * amplitude;
    };

    // Próbkowanie pola wysokości
    const dx = size / steps;
    const half = size / 2;
    const H = new Float32Array((steps + 1) * (steps + 1));
    let minH = Infinity, maxH = -Infinity;
    const id = (i, j) => j * (steps + 1) + i;
    for (let j = 0; j <= steps; j++) {
      const z = -half + j * dx;
      for (let i = 0; i <= steps; i++) {
        const x = -half + i * dx;
        const h = heightAt(x, z);
        H[id(i, j)] = h;
        if (h < minH) minH = h;
        if (h > maxH) maxH = h;
      }
    }

    // Generowanie linii poziomic metodą Marching Squares
    const group = new THREE.Group();
    scene.add(group);
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0x0d0d0d, transparent: true, opacity: 0.95 });

    const interp = (x1, z1, h1, x2, z2, h2, level) => {
      const t = (level - h1) / (h2 - h1);
      return [x1 + t * (x2 - x1), z1 + t * (z2 - z1)];
    };

    const addLevel = (level) => {
      const positions = [];
      for (let j = 0; j < steps; j++) {
        const z0 = -half + j * dx;
        const z1 = z0 + dx;
        for (let i = 0; i < steps; i++) {
          const x0 = -half + i * dx;
          const x1 = x0 + dx;
          const hBL = H[id(i, j)];
          const hBR = H[id(i + 1, j)];
          const hTR = H[id(i + 1, j + 1)];
          const hTL = H[id(i, j + 1)];
          const b0 = hBL > level ? 1 : 0;
          const b1 = hBR > level ? 2 : 0;
          const b2 = hTR > level ? 4 : 0;
          const b3 = hTL > level ? 8 : 0;
          const code = b0 | b1 | b2 | b3;
          if (code === 0 || code === 15) continue;

          const e = [
            [x0, z0, hBL, x1, z0, hBR],
            [x1, z0, hBR, x1, z1, hTR],
            [x1, z1, hTR, x0, z1, hTL],
            [x0, z1, hTL, x0, z0, hBL],
          ];
          const emit = (ea, eb) => {
            const a = e[ea], b = e[eb];
            const ap = interp(a[0], a[1], a[2], a[3], a[4], a[5], level);
            const bp = interp(b[0], b[1], b[2], b[3], b[4], b[5], level);
            positions.push(ap[0], level, ap[1], bp[0], level, bp[1]);
          };
          switch (code) {
            case 1: emit(3, 0); break;
            case 2: emit(0, 1); break;
            case 3: emit(3, 1); break;
            case 4: emit(1, 2); break;
            case 5: emit(3, 2); emit(0, 1); break;
            case 6: emit(0, 2); break;
            case 7: emit(3, 2); break;
            case 8: emit(2, 3); break;
            case 9: emit(2, 0); break;
            case 10: emit(1, 3); emit(2, 0); break;
            case 11: emit(1, 2); break;
            case 12: emit(1, 3); break;
            case 13: emit(1, 0); break;
            case 14: emit(3, 0); break;
            default: break;
          }
        }
      }
      if (positions.length) {
        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
        const lines = new THREE.LineSegments(geom, lineMaterial);
        lines.frustumCulled = false;
        group.add(lines);
      }
    };

    const levelsCount = 18;
    for (let l = 1; l <= levelsCount; l++) {
      const t = l / (levelsCount + 1);
      const level = minH + t * (maxH - minH);
      addLevel(level);
    }

    // Scroll -> przechył kamery
    let scrollTarget = 0;
    const onScroll = () => {
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - doc.clientHeight);
      maxScrollRef.current = max;
      scrollTarget = doc.scrollTop / max;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    let scrollT = 0;
    const lerp = (a, b, t) => a + (b - a) * t;
    const mapRangeClamp = (x, inMin, inMax, outMin, outMax) => {
      if (inMax === inMin) return outMin;
      if (x <= inMin) return outMin;
      if (x >= inMax) return outMax;
      const t = (x - inMin) / (inMax - inMin);
      return outMin + t * (outMax - outMin);
    };

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      scrollT = lerp(scrollT, scrollTarget, 0.08);

      camera.position.set(
        lerp(camTop.x, camSide.x, scrollT),
        lerp(camTop.y, camSide.y, scrollT),
        lerp(camTop.z, camSide.z, scrollT)
      );
      camera.lookAt(0, 0, 0);

      const scrollPx = scrollT * maxScrollRef.current;
      const start = thresholdsRef.current.start;
      const end = thresholdsRef.current.end;
      const op = mapRangeClamp(scrollPx, start, end, 0.7, 0.92);
      if (containerRef.current) containerRef.current.style.opacity = op.toFixed(3);

      // Aktualizuj clip-path co klatkę — śledzi pozycję elementu podczas scrolla
      updateClipRects();

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      updateThresholds();
      const doc = document.documentElement;
      maxScrollRef.current = Math.max(1, doc.scrollHeight - doc.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (containerRef.current) {
        try { containerRef.current.remove(); } catch (e) { /* ignore */ }
      }
      if (svgRef.current) {
        try { svgRef.current.remove(); } catch (e) { /* ignore */ }
      }
      if (group) {
        group.traverse((obj) => {
          if (obj.isLine) {
            if (obj.geometry) obj.geometry.dispose();
          }
        });
        scene.remove(group);
      }
      if (lineMaterial) lineMaterial.dispose();
      renderer.dispose();
    };
  }, [targetSelector]);

  return null;
};

export default SimpleAnimation;
